import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const Tvm = () => {
  const [presentValue, setPresentValue] = useState("");
  const [rateOfReturn, setRateOfReturn] = useState("");
  const [years, setYears] = useState("");
  const [futureValue, setFutureValue] = useState("0.00");

  // Function to calculate the Time Value of Money
  const calculateTVM = (presentValue, rateOfReturn, years) => {
    if (presentValue && rateOfReturn && years) {
      const futureVal =
        parseFloat(presentValue) *
        Math.pow(1 + parseFloat(rateOfReturn) / 100, parseFloat(years));
      setFutureValue(futureVal.toFixed(2));
    } else {
      setFutureValue("0.00");
    }
  };

  // useEffect to recalculate when input changes
  useEffect(() => {
    calculateTVM(presentValue, rateOfReturn, years);

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [presentValue, rateOfReturn, years]);

  // Handlers for input changes
  const handlePresentValueChange = (e) => {
    setPresentValue(e.target.value);
  };

  const handleRateOfReturnChange = (e) => {
    setRateOfReturn(e.target.value);
  };

  const handleYearsChange = (e) => {
    setYears(e.target.value);
  };

  return (
    <Layout
      title="TVM (Time Value of Money) Calculator"
      path="time-value-money"
    >
      <section className="tvm-section section-padding" id="tvm">
        <div className="container mt-5">
          <div className="row">
            {/* TVM Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Time Value of Money (TVM) Calculator
                </h3>
                <div className="container">
                  {/* Input Fields for TVM Calculation */}
                  <div className="row gy-3">
                    {/* Present Value */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="presentValue" className="form-label">
                        Present Value (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Present Value:</strong> The current value of the investment or money."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="presentValue"
                        placeholder="Enter present value"
                        value={presentValue}
                        onChange={handlePresentValueChange}
                      />
                      {/* <small className="form-text text-muted">Enter the current value of the investment</small> */}
                    </div>

                    {/* Rate of Return */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="rateOfReturn" className="form-label">
                        Rate of Return (%)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Rate of Return:</strong> The annual percentage rate of return expected from the investment."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="rateOfReturn"
                        placeholder="Enter rate of return"
                        value={rateOfReturn}
                        onChange={handleRateOfReturnChange}
                      />
                      {/* <small className="form-text text-muted">
                        Enter the expected annual rate of return
                      </small> */}
                    </div>

                    {/* Number of Years */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="years" className="form-label">
                        Number of Years
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of Years:</strong> The total duration of the investment in years."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="years"
                        placeholder="Enter number of years"
                        value={years}
                        onChange={handleYearsChange}
                      />
                      {/* <small className="form-text text-muted">
                        Enter the investment duration in years
                      </small> */}
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Estimated Future Value:{" "}
                        <span className="fw-bold ms-3">₹{futureValue}</span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Time Value of Money (TVM) Calculator
                </h3>

                {/* What is a TVM Calculator? */}
                <h4 className="mt-3 mb-3">What is a TVM Calculator?</h4>
                <p>
                  A **Time Value of Money (TVM) Calculator** helps estimate the
                  future value of an investment or savings based on the present
                  amount, expected rate of return, and duration.
                </p>

                {/* Why Use a TVM Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a TVM Calculator?</h4>
                <p>There are several benefits of using a TVM calculator:</p>
                <ul>
                  <li>
                    <strong>Investment Planning:</strong> Aids in estimating
                    future investment outcomes.
                  </li>
                  <li>
                    <strong>Retirement Preparation:</strong> Assists with
                    retirement savings goals.
                  </li>
                  <li>
                    <strong>Interest Impact:</strong> Demonstrates how different
                    rates of return affect total value.
                  </li>
                  <li>
                    <strong>Financial Forecasting:</strong> Useful for personal
                    or business financial planning.
                  </li>
                  <li>
                    <strong>Goal Setting:</strong> Helps set realistic long-term
                    financial targets.
                  </li>
                </ul>

                {/* How to Use a TVM Calculator? */}
                <h4 className="mt-4 mb-3">How to Use a TVM Calculator?</h4>
                <p>To use this calculator, follow these steps:</p>
                <ul>
                  <li>
                    Input the **Present Value** (₹) of your initial investment.
                  </li>
                  <li>
                    Enter the **Rate of Return** (%) as an annual percentage.
                  </li>
                  <li>
                    Specify the **Number of Years** for the investment period.
                  </li>
                  <li>
                    The calculator will instantly show the future value based on
                    your inputs.
                  </li>
                </ul>

                {/* Common Factors Influencing TVM */}
                <h4 className="mt-4 mb-3">Common Factors Influencing TVM</h4>
                <p>Key factors that affect the time value of money include:</p>
                <ul>
                  <li>
                    <strong>Present Value:</strong> The initial investment
                    amount.
                  </li>
                  <li>
                    <strong>Rate of Return:</strong> Higher rates yield greater
                    future value.
                  </li>
                  <li>
                    <strong>Time Duration:</strong> Longer periods increase
                    future value due to compounding.
                  </li>
                </ul>

                {/* Benefits of Using a TVM Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a TVM Calculator
                </h4>
                <p>Some key advantages include:</p>
                <ul>
                  <li>
                    <strong>Accurate Financial Projections:</strong> Avoids
                    underestimating future needs.
                  </li>
                  <li>
                    <strong>Improved Savings Strategy:</strong> Helps tailor
                    savings and investment plans.
                  </li>
                  <li>
                    <strong>Realistic Planning:</strong> Ensures more reliable
                    long-term goals.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Tvm;
